import React, { useEffect, useState } from 'react';

import { Button } from '@/design-system/button';
import { FormModal } from '@/design-system/form-modal';
import { Textarea } from '@/design-system/textarea';
import { toast } from '@/design-system/toaster';
import {
  EXCEPTION_REASON_LABELS,
  ExceptionReason,
  Invoice,
  invoicesAPI,
} from '@/services/api/invoices';

interface ExceptionModalProps {
  isOpen: boolean;
  invoice: Invoice | null;
  onClose: () => void;
  onUpdated: (invoice: Invoice) => void;
}

const REASON_OPTIONS: ExceptionReason[] = [
  'OUTLET_REFUSED',
  'DOC_LOST',
  'RIDER_CANNOT_RETURN',
  'OTHER',
];

const ExceptionModal: React.FC<ExceptionModalProps> = ({
  isOpen,
  invoice,
  onClose,
  onUpdated,
}) => {
  const [reason, setReason] = useState<ExceptionReason>('OUTLET_REFUSED');
  const [note, setNote] = useState('');
  const [autoFilledNote, setAutoFilledNote] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      const initialReason: ExceptionReason = 'OUTLET_REFUSED';
      const initialNote = EXCEPTION_REASON_LABELS[initialReason];
      setReason(initialReason);
      setNote(initialNote);
      setAutoFilledNote(initialNote);
      setIsSubmitting(false);
    }
  }, [isOpen]);

  const handleReasonClick = (opt: ExceptionReason) => {
    setReason(opt);
    const label = EXCEPTION_REASON_LABELS[opt];
    if (note.trim() === '' || note === autoFilledNote) {
      setNote(label);
      setAutoFilledNote(label);
    }
  };

  const handleNoteChange = (value: string) => {
    setNote(value);
    if (value !== autoFilledNote) {
      setAutoFilledNote(null);
    }
  };

  if (!isOpen || !invoice) return null;

  const canSubmit = note.trim().length > 0 && !isSubmitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setIsSubmitting(true);
    try {
      const updated = await invoicesAPI.flagException(
        invoice.id,
        reason,
        note.trim()
      );
      toast.success('Exception flagged', {
        description: `${updated.invoiceNumber} moved to exception.`,
      });
      onUpdated(updated);
      onClose();
    } catch (err) {
      toast.error('Could not flag exception', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <FormModal
      open={isOpen}
      onClose={onClose}
      icon={<i className="ri-alert-line" />}
      iconTone="danger"
      title="Flag exception"
      subtitle={`${invoice.invoiceNumber} · ${invoice.rider?.name ?? '—'} · ${invoice.client.name}`}
      size="lg"
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button tone="neutral" style="stroke" onClick={onClose}>
            Cancel
          </Button>
          <Button
            tone="destructive"
            onClick={handleSubmit}
            disabled={!canSubmit}
            loading={isSubmitting}
          >
            {isSubmitting ? 'Flagging…' : 'Flag exception'}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <div>
          <label className="mb-2 block text-[10px] font-bold uppercase tracking-[0.18em] text-text-sub-600">
            Reason
          </label>
          <div className="grid grid-cols-2 gap-2">
            {REASON_OPTIONS.map((opt) => {
              const active = opt === reason;
              return (
                <button
                  key={opt}
                  type="button"
                  onClick={() => handleReasonClick(opt)}
                  className={`rounded-lg border px-3 py-2 text-sm font-medium tracking-tight text-left transition-colors ${
                    active
                      ? 'border-accent text-accent'
                      : 'border-stroke-soft-200 text-text-sub-600 hover:bg-bg-weak-50'
                  }`}
                >
                  {EXCEPTION_REASON_LABELS[opt]}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <label
            htmlFor="exception-note"
            className="mb-2 block text-[10px] font-bold uppercase tracking-[0.18em] text-text-sub-600"
          >
            Note <span className="text-error-base">*</span>
          </label>
          <Textarea
            id="exception-note"
            rows={4}
            value={note}
            onChange={(e) => handleNoteChange(e.target.value)}
            placeholder="What happened? This note is logged to history."
          />
        </div>
      </div>
    </FormModal>
  );
};

export default ExceptionModal;
